import z from "zod";

const financeErrorTypeValues = [
  "PLACEHOLDER_ERROR",
  "STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE",
  "HOMEOWNER_CREDIT_ZERO_TOTAL_GSV",
  "HOMEOWNER_CREDIT_NEGATIVE_GSV_INPUT",
  "HOMEOWNER_CREDIT_ALLOCATION_EXCEEDS_GSV",
  "UNKNOWN_SUNLIGHT_STIPULATION_STATUS",
  "INVALID_DEALER_FEE_COMPUTATION",
] as const;

const financeErrorTypeSchema = z.enum(financeErrorTypeValues);
const FinanceErrorType = financeErrorTypeSchema.enum;
type FinanceErrorType = z.infer<typeof financeErrorTypeSchema>;

type ErrorDetails = {
  sentryDescription: string;
};

export type FinanceErrors =
  | {
      errorType: typeof FinanceErrorType.PLACEHOLDER_ERROR;
      // TODO: Add specific data fields for Finance errors
      data: Record<string, never>;
    }
  | {
      errorType: typeof FinanceErrorType.STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE;
      data: {
        reason: "missing" | "unexpected-fields";
        availableProducts?: { term: number; apr: number; productType: string; isACH: boolean }[];
        candidate?: {
          term: number;
          apr: number;
          productType: string;
          isACH: boolean;
          dealerFee?: number;
          promoAPR?: number;
          promoTerm?: number;
        };
      };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_ZERO_TOTAL_GSV;
      data: {
        amount: number;
        projectIds: string[];
      };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NEGATIVE_GSV_INPUT;
      data: {
        projectId: string;
        gsv: number;
      };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_ALLOCATION_EXCEEDS_GSV;
      data: {
        projectId: string;
        gsv: number;
        share: number;
        amount: number;
        sumGsvs: number;
      };
    }
  | {
      errorType: typeof FinanceErrorType.UNKNOWN_SUNLIGHT_STIPULATION_STATUS;
      data: {
        name: string;
        status: string;
      };
    }
  | {
      errorType: typeof FinanceErrorType.INVALID_DEALER_FEE_COMPUTATION;
      data: {
        reason:
          | "invalid-percentage"
          | "negative-cash-price"
          | "negative-submitted-amount"
          | "dealer-fee-exceeds-submitted-amount";
        dealerFeePercentage?: number;
        cashPrice?: number;
        submittedAmount?: number;
        dealerFee?: number;
      };
    };

export const financeErrorDetails: Record<FinanceErrorType, ErrorDetails> = {
  [FinanceErrorType.PLACEHOLDER_ERROR]: {
    sentryDescription: "Finance backend logic error",
  },
  [FinanceErrorType.STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE]: {
    sentryDescription:
      "Sunlight catalog does not contain the required Strategic Partner loan product (180-month, 6.99% APR, HII, ACH) or it carries unexpected fee/promo fields",
  },
  [FinanceErrorType.HOMEOWNER_CREDIT_ZERO_TOTAL_GSV]: {
    sentryDescription: "Homeowner credit allocation failed: sum of project GSVs is zero or negative",
  },
  [FinanceErrorType.HOMEOWNER_CREDIT_NEGATIVE_GSV_INPUT]: {
    sentryDescription: "Homeowner credit allocation failed: a project GSV input is negative",
  },
  [FinanceErrorType.HOMEOWNER_CREDIT_ALLOCATION_EXCEEDS_GSV]: {
    sentryDescription:
      "Homeowner credit allocation failed: a project's allocated share exceeds its GSV (would push post-allocation GSV negative)",
  },
  [FinanceErrorType.UNKNOWN_SUNLIGHT_STIPULATION_STATUS]: {
    sentryDescription:
      "Sunlight returned a stipulation status outside the known set (New, In Progress, Documents Received, Under Review, Completed) — add it to KNOWN_SUNLIGHT_STATUSES in reconcile-stipulations.ts",
  },
  [FinanceErrorType.INVALID_DEALER_FEE_COMPUTATION]: {
    sentryDescription:
      "Dealer fee gross-up received invalid inputs (percentage outside (0,1), negative cash price, or dealer fee exceeding submitted amount)",
  },
};
